import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircleSlash } from 'lucide-react';
import type {
  CreateFirewallRulesetVersionRequest,
  FirewallReplaySample,
  FirewallRulesetSummary,
  ReplayFirewallRulesetResponse,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { firewallIssuesFromError } from '../api/firewallRulesets.ts';
import {
  useActivateFirewallRuleset,
  useCreateFirewallRulesetVersion,
  useFirewallRuleset,
  useFirewallRulesetVersion,
  useReplayFirewallRuleset,
} from '../api/useFirewallRulesets.ts';
import { FirewallReplayPanel } from '../components/FirewallReplayPanel.tsx';
import { FirewallVersionDiff } from '../components/FirewallVersionDiff.tsx';
import { FirewallVersionEditor } from '../components/FirewallVersionEditor.tsx';
import { FirewallVersionHistory } from '../components/FirewallVersionHistory.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey, TParams } from '../i18n/index.ts';
import { diffFirewallRules } from '../lib/firewallRuleDiff.ts';
import {
  firewallActivationEvidence,
  type FirewallActivationEvidence,
} from '../lib/firewallReplayGate.ts';
import { Button, Chip, ConfirmDialog, PageHeader, Panel } from '../ui/index.ts';

/**
 * One ruleset: its version history, a diff against what is running, and the
 * only control on the console that changes live firewall behaviour (#1760).
 *
 * The two acts are kept visibly apart. Saving edits mints a draft version and
 * says so; activation is a separate button with its own confirmation that shows
 * the diff against the currently active version, because it is the moment every
 * request served by every binding referencing this ruleset starts being
 * evaluated against different rules.
 *
 * Since #1759 there is a third act between them: a version with no replay
 * covering the chain about to go live cannot be armed at all. The replay panel
 * is therefore part of the activation path, not a diagnostic sidebar — and when
 * the gate refuses, its explanation is shown WORD FOR WORD. It is the only
 * thing on the screen that knows which version is armed right now and what the
 * evidence was measured against; paraphrasing it into "please replay first"
 * would send an operator to re-run a replay they already have.
 */

function activeChip(ruleset: FirewallRulesetSummary): { tone: 'ok' | 'neutral'; key: string } {
  return ruleset.active_version === null
    ? { tone: 'neutral', key: 'unarmed' }
    : { tone: 'ok', key: 'active' };
}

type Translate = (key: I18nKey, params?: TParams) => string;

function versionLabel(version: number | null, t: Translate): string {
  return version === null ? t('firewall_rulesets.activate.evidence.nothing') : `v${String(version)}`;
}

/**
 * What to say about the evidence before the operator confirms.
 *
 * Every branch describes; none of them forbids. The server owns the decision
 * and can see things this cannot — a replay someone else ran, and a parent
 * activation that moved this chain's resolved hash.
 */
function evidenceCopy(
  evidence: FirewallActivationEvidence,
  activeVersion: number | null,
  t: Translate,
): { readonly text: string; readonly warn: boolean } {
  switch (evidence.kind) {
    case 'ungated':
      return {
        text: evidence.reason === 'disarm'
          ? t('firewall_rulesets.activate.evidence.disarm')
          : t('firewall_rulesets.activate.evidence.rearm'),
        warn: false,
      };
    case 'ready':
      return {
        text: t('firewall_rulesets.activate.evidence.ready', {
          version: evidence.record.version,
          id: evidence.record.id,
          baseline: versionLabel(evidence.record.baseline_version, t),
        }),
        warn: false,
      };
    case 'stale':
      return {
        text: t('firewall_rulesets.activate.evidence.stale', {
          baseline: versionLabel(evidence.record.baseline_version, t),
          active: versionLabel(activeVersion, t),
        }),
        warn: true,
      };
    default:
      return { text: t('firewall_rulesets.activate.evidence.missing'), warn: true };
  }
}

export function FirewallRulesetDetailPage(): JSX.Element {
  const t = useT();
  const { rulesetId = '' } = useParams<{ rulesetId: string }>();
  const detail = useFirewallRuleset(rulesetId);
  const createVersion = useCreateFirewallRulesetVersion();
  const activate = useActivateFirewallRuleset();
  const replay = useReplayFirewallRuleset();
  const toast = useToast();

  const ruleset = detail.data?.ruleset ?? null;
  const versions = detail.data?.versions ?? [];
  const activeVersion = ruleset?.active_version ?? null;
  const latestVersion = ruleset?.latest_version ?? null;

  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [activateTarget, setActivateTarget] = useState<number | null>(null);
  const [disarmOpen, setDisarmOpen] = useState(false);
  const [replayReport, setReplayReport] = useState<ReplayFirewallRulesetResponse | null>(null);
  /** The gate's own words for the last refusal, kept verbatim. */
  const [gateRefusal, setGateRefusal] = useState<string | null>(null);

  // Default to what is running, falling back to the newest draft when nothing
  // is armed — the two questions an operator opens this page with.
  const inspected = selectedVersion ?? activeVersion ?? latestVersion;
  const inspectedVersion = useFirewallRulesetVersion(rulesetId, inspected);
  const runningVersion = useFirewallRulesetVersion(rulesetId, activeVersion);

  const versionIssues = firewallIssuesFromError(createVersion.error);
  const diff = runningVersion.data === undefined || inspectedVersion.data === undefined
    ? null
    : diffFirewallRules(runningVersion.data.rules, inspectedVersion.data.rules);

  function saveVersion(body: CreateFirewallRulesetVersionRequest): void {
    createVersion.mutate({ ruleset_id: rulesetId, ...body }, {
      onSuccess: (result) => {
        if (result.version !== undefined) setSelectedVersion(result.version.version);
        toast.success(t('firewall_rulesets.toast.version_created'));
      },
      onError: (error) => {
        if (firewallIssuesFromError(error).length === 0) {
          toast.error(coerceErrorMessage(error));
        }
      },
    });
  }

  function runActivate(version: number | null): void {
    setGateRefusal(null);
    activate.mutate({ ruleset_id: rulesetId, version }, {
      onSuccess: () => {
        setActivateTarget(null);
        setDisarmOpen(false);
        toast.success(version === null
          ? t('firewall_rulesets.toast.disarmed')
          : t('firewall_rulesets.toast.activated', { version }));
      },
      onError: (error) => {
        // A gate refusal names the version the evidence was measured against and
        // the one armed now. That sentence is the whole remedy, and a toast
        // would take it away again — so it stays in the open dialog, unedited.
        if (error instanceof OpsApiError && error.code === 'FIREWALL_RULESET_REPLAY_REQUIRED') {
          setGateRefusal(error.message);
          return;
        }
        toast.error(coerceErrorMessage(error));
      },
    });
  }

  function runReplay(version: number, samples: readonly FirewallReplaySample[]): void {
    replay.mutate({ ruleset_id: rulesetId, version, samples }, {
      onSuccess: (result) => {
        setReplayReport(result);
        setGateRefusal(null);
      },
    });
  }

  if (detail.isLoading) {
    return <div className="h-40 animate-pulse rounded-lg bg-well" data-testid="firewall-ruleset-detail-loading" />;
  }
  if (detail.isError || ruleset === null) {
    return (
      <p className="m-0 text-body text-bad" role="alert" data-testid="firewall-ruleset-detail-error">
        {coerceErrorMessage(detail.error)}
      </p>
    );
  }

  const chip = activeChip(ruleset);
  const evidence = firewallActivationEvidence({
    targetVersion: activateTarget,
    activeVersion,
    record: replayReport?.record ?? null,
  });
  const evidenceText = evidenceCopy(evidence, activeVersion, t);

  return (
    <section className="flex flex-col gap-6" data-testid="firewall-ruleset-detail-page">
      <PageHeader
        title={ruleset.name}
        sub={ruleset.id}
        actions={
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <Link to="/supply/firewall-rulesets" className="text-label text-signal hover:underline">
              {t('firewall_rulesets.detail.back')}
            </Link>
            <Button
              variant="secondary"
              onClick={() => setDisarmOpen(true)}
              disabled={activate.isPending || activeVersion === null}
              data-testid="firewall-ruleset-disarm"
            >
              <CircleSlash aria-hidden="true" className="h-4 w-4" />
              <span>{t('firewall_rulesets.detail.disarm')}</span>
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="firewall-ruleset-status">
        <Chip tone={ruleset.scope === 'shared' ? 'signal' : 'neutral'} lamp={false}>
          {t(`firewall_rulesets.scope.${ruleset.scope}`)}
        </Chip>
        <Chip tone={chip.tone}>
          {chip.key === 'unarmed'
            ? t('firewall_rulesets.status.unarmed')
            : t('firewall_rulesets.status.active', { version: activeVersion ?? 0 })}
        </Chip>
        <span className="text-label text-paper-low">
          {activeVersion === null
            ? t('firewall_rulesets.detail.unarmed_hint')
            : t('firewall_rulesets.detail.armed_hint')}
        </span>
      </div>

      <Panel caption={t('firewall_rulesets.detail.versions')} testId="firewall-ruleset-versions">
        <FirewallVersionHistory
          versions={versions}
          activeVersion={activeVersion}
          selectedVersion={inspected}
          activating={activate.isPending}
          onSelect={setSelectedVersion}
          onActivate={(version) => {
            // Point the diff panel at the same version the confirmation is
            // about, so "what changes if I click yes" is answered by both.
            setSelectedVersion(version);
            setActivateTarget(version);
            setGateRefusal(null);
          }}
        />
      </Panel>

      <Panel
        caption={t('firewall_rulesets.detail.diff', {
          from: activeVersion ?? 0,
          to: inspected ?? 0,
        })}
        padded
        testId="firewall-ruleset-diff-panel"
      >
        {activeVersion === null ? (
          <p className="m-0 text-label text-paper-mid" data-testid="firewall-diff-no-active">
            {t('firewall_rulesets.diff.no_active')}
          </p>
        ) : (
          <FirewallVersionDiff entries={diff} testId="firewall-version-diff" />
        )}
      </Panel>

      {inspected === null ? null : (
        <Panel
          caption={t('firewall_rulesets.detail.replay', { version: inspected })}
          padded
          testId="firewall-ruleset-replay-panel"
        >
          <FirewallReplayPanel
            version={inspected}
            pending={replay.isPending}
            report={replayReport}
            errorMessage={replay.isError ? coerceErrorMessage(replay.error) : null}
            onSubmit={(samples) => runReplay(inspected, samples)}
          />
        </Panel>
      )}

      <Panel caption={t('firewall_rulesets.detail.editor')} padded testId="firewall-ruleset-editor-panel">
        {inspectedVersion.data === undefined ? (
          <div className="h-24 animate-pulse rounded-lg bg-well" />
        ) : (
          <FirewallVersionEditor
            key={inspectedVersion.data.version}
            baseVersion={inspectedVersion.data}
            pending={createVersion.isPending}
            serverIssues={versionIssues}
            onSubmit={saveVersion}
          />
        )}
      </Panel>

      <ConfirmDialog
        open={activateTarget !== null}
        onOpenChange={(next) => {
          if (!next && !activate.isPending) {
            setActivateTarget(null);
            setGateRefusal(null);
          }
        }}
        title={t('firewall_rulesets.activate.title', { version: activateTarget ?? 0 })}
        body={
          <div className="flex flex-col gap-2">
            <p className="m-0 text-body text-paper-mid">
              {t('firewall_rulesets.activate.body', { name: ruleset.name })}
            </p>
            <p className="m-0 text-label text-warn">{t('firewall_rulesets.activate.blast')}</p>
            <p
              className={evidenceText.warn ? 'm-0 text-label text-warn' : 'm-0 text-label text-paper-mid'}
              data-testid="firewall-activate-evidence"
            >
              {evidenceText.text}
            </p>
            <p className="m-0 text-micro text-paper-low">
              {t('firewall_rulesets.activate.evidence.advisory')}
            </p>
            {gateRefusal === null ? null : (
              <div
                className="flex flex-col gap-1 rounded-lg bg-well p-3"
                role="alert"
                data-testid="firewall-activate-gate-refusal"
              >
                <span className="text-label text-bad">
                  {t('firewall_rulesets.activate.gate.title')}
                </span>
                <span className="text-micro text-paper-low">
                  {t('firewall_rulesets.activate.gate.hint')}
                </span>
                <p className="m-0 text-label text-paper" data-testid="firewall-activate-gate-message">
                  {gateRefusal}
                </p>
              </div>
            )}
            {activeVersion === null ? (
              <p className="m-0 text-label text-paper-mid">
                {t('firewall_rulesets.diff.no_active')}
              </p>
            ) : inspectedVersion.data === undefined || runningVersion.data === undefined ? (
              <p className="m-0 text-label text-paper-low">{t('common.loading')}</p>
            ) : (
              <FirewallVersionDiff entries={diff} testId="firewall-activate-diff" />
            )}
          </div>
        }
        tone="danger"
        confirmLabel={t('firewall_rulesets.activate.confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={activate.isPending}
        onConfirm={() => runActivate(activateTarget)}
        testId="firewall-ruleset-activate-confirm"
      />

      <ConfirmDialog
        open={disarmOpen}
        onOpenChange={(next) => {
          if (!next && !activate.isPending) setDisarmOpen(false);
        }}
        title={t('firewall_rulesets.disarm.title')}
        body={t('firewall_rulesets.disarm.body')}
        tone="danger"
        confirmLabel={t('firewall_rulesets.disarm.confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={activate.isPending}
        onConfirm={() => runActivate(null)}
        testId="firewall-ruleset-disarm-confirm"
      />
    </section>
  );
}
